import {compact, groupByUniqueKey, partition} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useState} from 'react';

import {pluralize} from 'BenchlingUtil';
import {convertStrandToDisplayName, CustomField} from 'constants/MolbioConstants';
import geneticCodesDNA from 'data/geneticCodesDNA.json';
import {BulkEditTranslationRegionGroupData} from 'molbio/dao/SequenceDAO';
import FeatureTableChipList from 'molbio/sequence-features/FeatureTableChipList';
import {modelEndToDisplay, modelStartToDisplay} from 'molbio/SeqUtil';
import {TranslationRegionGroupData} from 'molbio/translations/TranslationUtil';
import {FONT_PROXIMA_19} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import Tooltip from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import ButtonGroup from 'ui-library/buttons/ButtonGroup';
import {ChipSize} from 'ui-library/chips/Chip';
import {getChipsRowHeight} from 'ui-library/chips/ChipList';
import {GRAY_DARKER} from 'ui-library/Colors';
import {ColorSwatch} from 'ui-library/ColorSwatch';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import legacyRunModal from 'ui-library/legacyRunModal';
import {
  RowSelectionMode,
  ScrollableTableColumn,
  ScrollableTableRow,
  TABLE_KEYS,
  TABLE_ROW_BODY_HEIGHT,
} from 'ui-library/scrollable/ScrollableTableConstants';
import SortableScrollableTable from 'ui-library/scrollable/SortableScrollableTable';

import TranslationBulkEditModal from './TranslationBulkEditModal';

interface TranslationRowData {
  name: string;
  start: number;
  end: number;
  aminoAcidLength: number;
  strandText: string;
  displayRegions: JSX.Element;
  regionTexts: Array<string>;
  notes: string;
  customFields: Array<CustomField>;
  customFieldsElement: JSX.Element;
  geneticCodeName: string;
  color: string;
  colorElement: JSX.Element;
  coerceStartCodonToMethionine: boolean;
  formattedStartCodonDetails: string;
}

export interface TranslationRow extends ScrollableTableRow {
  data: TranslationRowData;
}

// Temporarily a function so that BenchlingConfig can be stubbed between tests.
function getTranslationColumns(): Array<ScrollableTableColumn> {
  return compact([
    {
      header: 'Name',
      accessorKey: 'name',
      isSortable: true,
      sortKey: 'name',
    },
    {
      header: 'Start',
      accessorKey: 'start',
      isSortable: true,
      sortKey: 'start',
    },
    {
      header: 'End',
      accessorKey: 'end',
      isSortable: true,
      sortKey: 'end',
    },
    {
      header: '# of AAs',
      accessorKey: 'aminoAcidLength',
      isSortable: true,
      sortKey: 'aminoAcidLength',
    },
    {
      header: 'Regions',
      accessorKey: 'displayRegions',
      isSortable: false,
    },
    {
      header: 'Strand',
      accessorKey: 'strandText',
      isSortable: true,
      sortKey: 'strandText',
    },
    {
      header: 'Genetic code',
      accessorKey: 'geneticCodeName',
      isSortable: true,
      sortKey: 'geneticCodeName',
    },
    {
      header: 'Start codon',
      accessorKey: 'formattedStartCodonDetails',
      isSortable: true,
      sortKey: 'coerceStartCodonToMethionine',
    },
    {
      header: 'Color',
      accessorKey: 'colorElement',
      isSortable: true,
      sortKey: 'color',
    },
    {
      header: 'Notes',
      accessorKey: 'notes',
      isSortable: true,
      sortKey: 'notes',
    },
    {
      header: 'Custom fields',
      accessorKey: 'customFieldsElement',
      isSortable: false,
    },
  ]);
}

export interface ExpandedTranslationTabProps {
  translationRegionGroups: Array<TranslationRegionGroupData>;
  sequenceStartIndex: number;
  sequenceLength: number;
  isSingleStranded: boolean;
  allowFeatureEdits: boolean;
  deleteTranslations: (translationIds: Array<number>, successMessage: string) => void;
  bulkEditTranslations: (data: Array<BulkEditTranslationRegionGroupData>) => void;
}

function ExpandedTranslationTab({
  translationRegionGroups,
  sequenceStartIndex,
  sequenceLength,
  isSingleStranded,
  allowFeatureEdits,
  deleteTranslations,
  bulkEditTranslations,
}: ExpandedTranslationTabProps): JSX.Element {
  const [selectedIds, setSelectedIds] = useState<Array<string>>([]);
  const rows = translationRegionGroups.map((group) =>
    getTranslationRow(sequenceStartIndex, sequenceLength, group)
  );
  const translationRegionGroupsByCid: {
    [cid: string]: TranslationRegionGroupData;
  } = groupByUniqueKey(translationRegionGroups, (model) => model.cid);
  const selectedPartTranslationIds = selectedIds.filter((id) => !translationRegionGroupsByCid[id]?.editable);

  useEffect(() => {
    if (!allowFeatureEdits) {
      setSelectedIds([]);
    }
  }, [allowFeatureEdits]);

  useEffect(() => {
    // After updating lone translations, new groups are created that result in ID changes
    // For now, remove the IDs that no longer exist from selected but we may want to map the old
    // translation to the new group in the future
    const [validIds, removeIds] = partition(selectedIds, (id) => translationRegionGroupsByCid[id] != null);
    if (removeIds.length > 0) {
      setSelectedIds(validIds);
    }
  }, [translationRegionGroups, selectedIds, translationRegionGroupsByCid]);

  function getTranslationRow(
    sequenceStartIndex: number,
    sequenceLength: number,
    group: TranslationRegionGroupData
  ): TranslationRow {
    const regionTexts =
      group.translations.length <= 1
        ? []
        : group.translations.map(
            ({start, end}) =>
              `${modelStartToDisplay(start, sequenceStartIndex)}-${modelEndToDisplay(
                end,
                sequenceLength,
                sequenceStartIndex
              )}`
          );
    return {
      id: group.cid,
      isSelected: selectedIds.includes(group.cid),
      data: {
        name: `${group.name}\u00a0`,
        start: modelStartToDisplay(group.translations[0].start, sequenceStartIndex),
        end: modelEndToDisplay(
          group.translations[group.translations.length - 1].end,
          sequenceLength,
          sequenceStartIndex
        ),
        aminoAcidLength: group.aminoAcidLength,
        strandText: convertStrandToDisplayName(group.strand),
        displayRegions: <FeatureTableChipList items={regionTexts} />,
        regionTexts,
        notes: group.notes,
        customFields: group.customFields,
        customFieldsElement: (
          <FeatureTableChipList items={group.customFields.map(({name, value}) => `${name}: ${value}`)} />
        ),
        geneticCodeName: geneticCodesDNA[group.codonTranslationTable].name,
        color: group.color,
        colorElement: <ColorSwatch fillColor={group.color} darkerBorder={false} small={true} />,
        coerceStartCodonToMethionine: group.coerceStartCodonToMethionine,
        formattedStartCodonDetails: group.coerceStartCodonToMethionine
          ? 'Translate as methionine'
          : 'Unspecified',
      },
    };
  }

  function getRowHeight(rowIdx: number): number {
    if (rowIdx >= rows.length) {
      return TABLE_ROW_BODY_HEIGHT;
    }
    const maxChips = Math.max(rows[rowIdx].data.regionTexts.length, rows[rowIdx].data.customFields.length);
    return getChipsRowHeight(maxChips, ChipSize.TABLE_CELL);
  }

  function handleSelection(toggledRows: Array<TranslationRow>, isSelected: boolean): void {
    const toggledIds = toggledRows.map((r: TranslationRow) => r.id);
    if (isSelected) {
      const newlySelected = toggledIds.filter((id) => !selectedIds.includes(id));
      setSelectedIds([...selectedIds, ...newlySelected]);
    } else {
      setSelectedIds(selectedIds.filter((id) => !toggledIds.includes(id)));
    }
  }

  // Deletes all translation regions within selected groups to delete a translation region group
  function onClickDeleteTranslations(): void {
    const groupsToDelete = translationRegionGroups.filter(({cid}) => selectedIds.includes(cid));
    const translationIdsToDelete = groupsToDelete.flatMap((group) => group.translations.map(({id}) => id));
    // Need to pass in an explicit success message since the message from the backend counts
    // all translation regions separately, which is inconsistent with the new language for translations
    deleteTranslations(
      translationIdsToDelete,
      `Successfully deleted ${groupsToDelete.length} ${pluralize(groupsToDelete.length, 'translation')}`
    );
  }

  return (
    <div
      className={css(styles.tabContainer)}
      {...getTestProps({component: 'ExpandedTranslationTab', element: 'root'})}
    >
      <div className={css(styles.tabHeaderContainer)}>
        <p className={css(styles.tabHeader)}>Translations</p>
        <p className={css(styles.tabSubheader)}>{`${translationRegionGroups.length} total`}</p>
      </div>
      <div className={css(styles.actionsContainer)}>
        {selectedPartTranslationIds.length > 0 && (
          <div className={css(styles.partsInfoContainer)}>
            <p className={css(styles.partsInfoText)}>{`${selectedPartTranslationIds.length} ${pluralize(
              selectedPartTranslationIds.length,
              'part translation'
            )} selected`}</p>
            <Tooltip title="Cannot edit or delete translations that are inside parts">
              <BenchlingIcon icon="info-circle" />
            </Tooltip>
          </div>
        )}
        <ButtonGroup>
          <Tooltip title="Edit">
            <Button
              icon="edit"
              buttonStyle={ButtonStyle.TERTIARY_GRAY}
              size={ButtonSize.SMALL}
              onClick={() => {
                legacyRunModal(TranslationBulkEditModal, {
                  selectedTranslationRegionGroups: selectedIds.map((id) => translationRegionGroupsByCid[id]),
                  sequenceStartIndex,
                  sequenceLength,
                  isSingleStranded,
                  bulkEditTranslations,
                });
              }}
              disabled={
                !allowFeatureEdits || selectedPartTranslationIds.length > 0 || selectedIds.length === 0
              }
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              icon="delete"
              buttonStyle={ButtonStyle.NEGATIVE}
              size={ButtonSize.SMALL}
              onClick={onClickDeleteTranslations}
              disabled={
                !allowFeatureEdits || selectedPartTranslationIds.length > 0 || selectedIds.length === 0
              }
            />
          </Tooltip>
        </ButtonGroup>
      </div>
      <SortableScrollableTable
        className={css(styles.table)}
        canResizeColumns={true}
        rowSelectionMode={RowSelectionMode.MULTIPLE}
        tableKey={TABLE_KEYS.EXPANDED_TRANSLATION_TABLE}
        disableSelection={!allowFeatureEdits}
        columns={getTranslationColumns()}
        getRowContentHeight={getRowHeight}
        isCompact={true}
        emptyTableText="This sequence has no translations"
        onRowsSelect={handleSelection}
        initialRows={rows}
        initialSort={[{sortKey: 'start'}]}
        getSortValue={(row: TranslationRow, sortKey: keyof TranslationRowData) => row.data[sortKey] as string}
        matchParentHeight={true}
      />
    </div>
  );
}

const styles = StyleSheet.create({
  tabContainer: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  tabHeaderContainer: {
    display: 'flex',
    alignItems: 'baseline',
  },
  tabHeader: FONT_PROXIMA_19,
  tabSubheader: {
    color: GRAY_DARKER,
    marginLeft: '10px',
  },
  actionsContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: '8px',
  },
  partsInfoContainer: {
    display: 'flex',
    alignItems: 'center',
    marginRight: '8px',
    color: GRAY_DARKER,
  },
  partsInfoText: {
    marginBottom: '0',
    marginRight: '4px',
  },
  table: {
    height: '100%',
  },
});

export default ExpandedTranslationTab;
